// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, EMPTY, forkJoin, merge, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, concatAll, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { flatten } from "ramda"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import { throttleSame } from "../../../lib/rxutils"
import * as TimelineActions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineStatsSlice from "../slices/stats"
import type { RootState } from "app/store"
import { planTimelineFetchOperations, TimelineFetchOperations } from "./lib/timelineFetchOperations"
import { DateTime } from "luxon"
import { patchDevelopmentUrls } from "../devUrls"
import { DateRange, TimelineDate } from "../dateTime"
import { showSnackBar } from "../../snackbar/actions"
import { ToastOptions } from "react-toastify"
import { processTimesheetFieldData } from "./lib/processTimesheetFieldData"
import { executor, Init, timelineService } from "../../../app/rpc"
import {
  ListDaySummariesRequestSchema,
  ListDaySummariesResponse,
  ListTimelinesRequestSchema,
  ListTimelinesResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

const prepareTimelineFetchOperations = (action: { payload: TimelineDate | undefined }, state: RootState): TimelineFetchOperations =>
  planTimelineFetchOperations(state, DateTime.local)(action.payload)

type ListTimelinesRpc = (request: Init<typeof ListTimelinesRequestSchema>) => Observable<ListTimelinesResponse>
type ListDaySummariesRpc = (request: Init<typeof ListDaySummariesRequestSchema>) => Observable<ListDaySummariesResponse>

export const getTimelineData = (
  listTimelinesRpc: ListTimelinesRpc,
  listDaySummariesRpc: ListDaySummariesRpc,
) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.getTimelineData.match),
      throttleSame(100, (a, b) => a.payload === b.payload),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const fetchOperations = prepareTimelineFetchOperations(action, state)
        const rangeOfRequestedTimeline: DateRange = {
          startDateLocal: fetchOperations.requestedTimeline,
          endDateLocal: fetchOperations.requestedTimeline,
        }
        const actions = merge(
          listTimelines({
            listTimelinesRpc,
            requestedTimeline: rangeOfRequestedTimeline,
            ignoreError: false,
            isActiveDate: true,
            state,
          }),
          listDaySummaries(listDaySummariesRpc, fetchOperations.daySummaries, false),
          3,
        )
        return concat(
          // Fetch the requested timeline data, in parallel. If the requested
          // timeline data calls fail, the whole epic should fail, and show a message
          // to the user saying that we were unable to fetch their timeline data
          actions,
          // When responses for all are back, fetch the extra timelines (there will be up to 2) in
          // parallel. If the extra timelines call fails, don't display an error. This is non-fatal
          forkJoin(
            flatten(
              fetchOperations.extraTimelines.map(extraTimeline => [
                listTimelines({
                  listTimelinesRpc,
                  requestedTimeline: extraTimeline,
                  ignoreError: true,
                  state,
                }),
              ])
            )
          ).pipe(concatAll()),
        )
      }),
    )

const options: ToastOptions = {
  autoClose: 10000,
}

type ListTimelinesPayload = {
  listTimelinesRpc: ListTimelinesRpc,
  requestedTimeline: DateRange,
  ignoreError: boolean,
  isActiveDate?: boolean,
  state: RootState,
}
const listTimelines = ({
  listTimelinesRpc,
  requestedTimeline,
  ignoreError,
  isActiveDate,
  state,
}: ListTimelinesPayload): Observable<Action> =>
  of(requestedTimeline).pipe(
    map(r => ({
      startDate: r.startDateLocal.toString(),
      endDate: r.endDateLocal.toString(),
    })),
    mergeMap(listTimelinesRpc),
    map(r => r.timelines.map(t => patchDevelopmentUrls(t))),
    mergeMap(timelines => {
      const actions: Action[] = [
        timelines.length === 0 && isActiveDate
          ? TimelineSlice.resetLoadingAndRefreshRequired()
          : TimelineSlice.setTimeline(timelines),
      ]
      const timeGroups = flatten(timelines.map(t => t.timeGroupsInRange))
      const timeRows = flatten(timelines.map(t => t.timeRowsInRange))
      const timesheetFieldActions = processTimesheetFieldData(timeGroups, timeRows, state)
      actions.push(...timesheetFieldActions)
      return concat(actions)
    }),
    catchError(_ => concat(
      ignoreError ? EMPTY : of(showSnackBar({
        message: t({ id: "msg_get_timeline_failed", message: "Failed to get the timeline. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
        messageType: "ERROR",
        options,
      })),
      of(TimelineSlice.setLoading(false)),
    ))
  )

const listDaySummaries = (listDaySummariesRpc: ListDaySummariesRpc, daySummaries: DateRange, ignoreError: boolean): Observable<Action> =>
  of(daySummaries).pipe(
    map(_ => ({
      startDate: daySummaries.startDateLocal.toString(),
      endDate: daySummaries.endDateLocal.toString(),
    })),
    mergeMap(listDaySummariesRpc),
    map(t =>
      ({
        response: t,
        range: daySummaries
      })),
    map(TimelineStatsSlice.setDaySummaries),
    catchError(_ => ignoreError ? EMPTY : of(showSnackBar({
      message: t({ id: "msg_get_day_summaries_failed", message: "Failed to get day summaries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
      options,
    }))
    )
  )

const listTimelinesRpc = (request: Init<typeof ListTimelinesRequestSchema>): Observable<ListTimelinesResponse> =>
  executor(() => timelineService.listTimelines(create(ListTimelinesRequestSchema, request)))

const listDaySummariesRpc = (request: Init<typeof ListDaySummariesRequestSchema>): Observable<ListDaySummariesResponse> =>
  executor(() => timelineService.listDaySummaries(create(ListDaySummariesRequestSchema, request)))

export default getTimelineData(listTimelinesRpc, listDaySummariesRpc)
